import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Suspense } from 'react';

// Import Bootstrap CSS first
import 'bootstrap/dist/css/bootstrap.min.css';
// Import Bootstrap Icons
import 'bootstrap-icons/font/bootstrap-icons.css';
// Then import our custom CSS
import './App.css';

// Lazy-loaded Components
import * as Lazy from './components/LazyComponents';

// Loading Component
import LoadingSpinner from './components/UI/LoadingSpinner';

// Version Update Components
import VersionUpdateModal, { useVersionUpdate } from './components/UI/VersionUpdateModal';

// Deep Link Handler
import DeepLinkHandler from './components/common/DeepLinkHandler';

// Error Boundary
import ErrorBoundary from './components/common/ErrorBoundary';

// Services
import { authService } from './services/authService';

// Hooks
import { useSafeAreaTopPadding } from './hooks/useSafeAreaTopPadding';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  useSafeAreaTopPadding();

  const [isAuthChecked, setIsAuthChecked] = React.useState(false);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const { showUpdateModal, handleUpdate, handleDismiss } = useVersionUpdate();

  React.useEffect(() => {
    (async () => {
      const result = await authService.isAuthenticated();
      setIsAuthenticated(result);
      setIsAuthChecked(true);
    })();
  }, []);

  if (!isAuthChecked) {
    return <LoadingSpinner />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <div className="App">
        {/* Deep Link Handler - Initialize early in the router context */}
        <ErrorBoundary>
          <DeepLinkHandler />
        </ErrorBoundary>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Public Routes */}
            <Route 
              path="/login" 
              element={
                isAuthenticated ? <Navigate to="/dashboard" replace /> : <Lazy.Login />
              } 
            />
            <Route path="/about" element={<Lazy.About />} />
            <Route path="/contact-us" element={<Lazy.ContactUs />} />
            <Route path="/privacy-policy" element={<Lazy.PrivacyPolicy />} />

            {/* Protected Routes */}
            <Route element={<Lazy.ProtectedRoute />}> 
              <Route element={<Lazy.Layout />}> 
                <Route path="/dashboard" element={<Lazy.Dashboard />} />
                <Route path="/students" element={<Lazy.StudentList />} />
                <Route path="/students/new" element={<Lazy.StudentForm />} />
                <Route path="/students/:id/edit" element={<Lazy.StudentForm isEdit={true} />} />
                <Route path="/students/:id" element={<Lazy.StudentProfile />} />
                <Route path="/cases" element={<Lazy.CaseList />} />
                <Route path="/cases/:id" element={<Lazy.CaseDetails />} />
                <Route path="/schools" element={<Lazy.SchoolList />} />
                <Route path="/reports" element={<Lazy.SystemReports />} />
                <Route path="/activities" element={<Lazy.ActivityList />} />
                {/* Admin Routes - Protected by role-based access within components */}
                <Route path="/admin/users" element={<Lazy.UserManagement />} />
                <Route path="/admin/schools" element={<Lazy.SchoolManagement />} />
                <Route path="/admin/reports" element={<Lazy.SystemReports />} />
                {/* Development-only routes */}
                {process.env.NODE_ENV === 'development' && (
                  <Route path="/debug/deeplinks" element={<Lazy.DeepLinkTestPage />} />
                )}
              </Route>
            </Route>

            {/* Default redirect */}
            <Route 
              path="/" 
              element={
                <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />
              } 
            />
            
            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
        
        {/* Version Update Modal */}
        <VersionUpdateModal
          show={showUpdateModal}
          onHide={handleDismiss}
          onUpdate={handleUpdate}
        />
      </div>
      
      {/* React Query DevTools */}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
